/**
 * PrimeNG p-multiselect accessibility utilities.
 */

const OBJECT_STRING = "[object Object]";

/**
 * Sanitize the accessible value of PrimeNG p-multiselect combobox inputs
 * (WCAG 4.1.2 Name, Role, Value).
 *
 * PrimeNG renders a hidden combobox `<input role="combobox">` inside
 * `.p-hidden-accessible` and binds `[attr.value]="modelValue()"` on it. When a
 * multiselect uses an OBJECT `optionValue` (e.g. `optionValue="value"` resolves
 * to a nested object), `modelValue()` is an array of objects, so Angular
 * stringifies it to `"[object Object]"` (or `"[object Object],[object Object]"`
 * for several selections). JAWS reads a combobox input's `value`, so it
 * announces the meaningless "[object Object]" text reported in the ticket.
 *
 * This walks the host for combobox inputs and, whenever their `value` attribute
 * contains "[object Object]", replaces it with the human-readable selection
 * text already shown in the visible `.p-multiselect-label` (falling back to
 * removing the attribute when there is no selection text). A MutationObserver
 * keeps the value clean because PrimeNG re-writes it on every selection change
 * and change-detection pass.
 *
 * Pair this with a meaningful `[ariaLabel]` on each `<p-multiselect>` so screen
 * readers announce the field name, current value and expand/collapse state
 * using only user-friendly text.
 *
 * Call from `ngAfterViewInit` with the component host element
 * (`ElementRef.nativeElement`) and disconnect the returned observer in
 * `ngOnDestroy`.
 */
export function sanitizeMultiselectComboboxValue(
  hostEl: HTMLElement | null | undefined
): MutationObserver | null {
  if (!hostEl || typeof MutationObserver === "undefined") {
    return null;
  }

  const clean = (): void => {
    // Scope to PrimeNG's own hidden accessible trigger input (wrapped in
    // `.p-hidden-accessible`), not any other `role="combobox"` input that may
    // exist inside the panel (e.g. the esp-multi-select-filter search box
    // also uses `role="combobox"` for its own unrelated aria-activedescendant
    // wiring and must keep its own accessible name).
    hostEl.querySelectorAll('.p-hidden-accessible input[role="combobox"]').forEach((node) => {
      const input = node as HTMLElement;
      const value = input.getAttribute("value");
      if (value === null || !value.includes(OBJECT_STRING)) {
        return;
      }
      const label = input
        .closest("p-multiselect")
        ?.querySelector(".p-multiselect-label")
        ?.textContent?.trim();
      if (label) {
        input.setAttribute("value", label);
      } else {
        input.removeAttribute("value");
      }
    });
  };

  clean();

  const observer = new MutationObserver(clean);
  observer.observe(hostEl, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["value"],
  });
  return observer;
}

const BASE_LABEL_ATTR = "data-esp-base-label";
const LAST_LABEL_ATTR = "data-esp-last-label";

/**
 * Make the accessible NAME of PrimeNG p-multiselect combobox inputs contain the
 * visible selection text (WCAG 2.5.3 Label in Name).
 *
 * PrimeNG applies our descriptive `[ariaLabel]` (e.g. "CTs" / "Management
 * Units") as the combobox `aria-label`, but that name does NOT contain the
 * visible selected-value text shown in `.p-multiselect-label` (e.g. "3 Email
 * (Back Office)"). Dragon NaturallySpeaking matches the text the user sees, so
 * "Click 3 Email (Back Office)" could not target the control.
 *
 * This captures the original descriptive label ONCE (stored in a data attribute
 * so it is never doubled on re-runs) and rewrites `aria-label` to
 * "<descriptive label>, <visible selection>". The combined name stays unique
 * and descriptive AND contains the visible label, so Dragon users can target
 * the combo box by either "Click CTs" or "Click 3 Email (Back Office)". A
 * MutationObserver re-applies it whenever the selection or tokens change.
 *
 * The same combined name is ALSO mirrored onto the VISIBLE `.p-multiselect`
 * trigger container. PrimeNG puts the accessible name on the hidden
 * `.p-hidden-accessible input[role="combobox"]`, but that input is clipped to a
 * ~0x0 box, so Dragon (which activates a control by clicking its on-screen
 * rectangle) has no real target to click. The visible `.p-multiselect`
 * container has the real bounding box and its own click handler (PrimeNG's
 * `onContainerClick` opens the panel), but no accessible name, so "Click CTs"
 * matched a named-but-unclickable element and a clickable-but-unnamed element -
 * neither worked. Mirroring the name onto the visible container gives Dragon a
 * named target with a real bounding box. The container has no ARIA role, so
 * naming it does not add a duplicate announcement for screen readers (they name
 * the focusable combobox input, not the role-less container div).
 *
 * Call from `ngAfterViewInit` with the component host element and disconnect the
 * returned observer in `ngOnDestroy`.
 */
export function associateMultiselectVisibleLabel(
  hostEl: HTMLElement | null | undefined
): MutationObserver | null {
  if (!hostEl || typeof MutationObserver === "undefined") {
    return null;
  }

  const apply = (): void => {
    // Scope to PrimeNG's own hidden accessible trigger input (wrapped in
    // `.p-hidden-accessible`), not any other `role="combobox"` input that may
    // exist inside the panel (e.g. the esp-multi-select-filter search box
    // also uses `role="combobox"` for its own unrelated aria-activedescendant
    // wiring). Without this scoping, this rewrites the search input's
    // aria-label to the visible selection text, overriding its own
    // `<label for>` and breaking Dragon voice control's ability to target it
    // by its visible "Search ..." label.
    hostEl.querySelectorAll('.p-hidden-accessible input[role="combobox"]').forEach((node) => {
      const input = node as HTMLElement;
      const currentLabel = input.getAttribute("aria-label");
      // Capture the descriptive label as the base whenever it was applied
      // externally (first run, or a runtime locale change via TranslateService)
      // rather than composed by us, so it is not frozen to the old language.
      if (
        currentLabel !== null &&
        currentLabel !== input.getAttribute(LAST_LABEL_ATTR)
      ) {
        input.setAttribute(BASE_LABEL_ATTR, currentLabel);
      }
      const baseLabel = input.getAttribute(BASE_LABEL_ATTR) ?? "";
      const visibleLabel = input
        .closest("p-multiselect")
        ?.querySelector(".p-multiselect-label")
        ?.textContent?.trim() ?? "";
      const name = [baseLabel, visibleLabel].filter(Boolean).join(", ");
      if (name && currentLabel !== name) {
        input.setAttribute("aria-label", name);
        input.setAttribute(LAST_LABEL_ATTR, name);
      }
      // Mirror the combined name onto the VISIBLE `.p-multiselect` trigger so
      // Dragon has a named, real-sized, clickable target (the hidden combobox
      // input that carries the name is clipped to ~0x0 and cannot be clicked by
      // voice). The container is role-less, so this does not duplicate the
      // screen-reader announcement of the focusable combobox input.
      const container = input.closest(".p-multiselect");
      if (name && container && container.getAttribute("aria-label") !== name) {
        container.setAttribute("aria-label", name);
      }
    });
  };

  apply();

  const observer = new MutationObserver(apply);
  observer.observe(hostEl, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["value", "aria-label"],
  });
  return observer;
}

/**
 * Expose a PrimeNG p-multiselect overlay as a SINGLE-select control to assistive
 * technology (WCAG 4.1.2 Name, Role, Value).
 *
 * The Plan Detail CT dropdown is functionally single-select — picking an option
 * replaces the whole selection and closes the panel (see `updateSelectedCTs`) —
 * and its option checkboxes are hidden visually (`.p-multiselect.fullrow
 * .p-multiselect-option > .p-checkbox { display:none }`). But it is still built
 * on `p-multiselect`, and PrimeNG v21 HARDCODES `role="listbox"
 * aria-multiselectable="true"` on the option `<ul>` and binds `aria-checked` on
 * every `<li role="option">`. There is no component input to change either, so a
 * screen reader wrongly announces a multi-select checkbox list.
 *
 * Given the overlay panel element, this flips the option listbox to
 * `aria-multiselectable="false"` and strips the redundant `aria-checked` from
 * each option, so the control is announced as a single-select listbox. A
 * MutationObserver re-applies both because PrimeNG re-adds `aria-checked` on
 * selection/change-detection and virtual scrolling recycles the option rows.
 *
 * The panel is `appendTo="body"`, so pass the overlay element (looked up by a
 * unique `panelStyleClass`) — NOT the component host. Call on the multiselect
 * `(onPanelShow)` and disconnect the returned observer on `(onPanelHide)` /
 * `ngOnDestroy`.
 */
export function enforceMultiselectSingleSelectAria(
  panelEl: HTMLElement | null | undefined
): MutationObserver | null {
  if (!panelEl || typeof MutationObserver === "undefined") {
    return null;
  }

  const apply = (): void => {
    panelEl
      .querySelectorAll('ul[role="listbox"][aria-multiselectable="true"]')
      .forEach((list) => {
        (list as HTMLElement).setAttribute("aria-multiselectable", "false");
      });
    panelEl.querySelectorAll('[role="option"][aria-checked]').forEach((option) => {
      (option as HTMLElement).removeAttribute("aria-checked");
    });
  };

  apply();

  const observer = new MutationObserver(apply);
  observer.observe(panelEl, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["aria-checked", "aria-multiselectable"],
  });
  return observer;
}
